import { useState } from "react";
import { AlertTriangle, Upload } from "lucide-react";
import { PageHeader } from "../../components/layout/PageHeader";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { fmtDateTime } from "../../components/reports/ReportBits";

/**
 * TEMPORARY admin tool: migrate Fonix IVR entry history (web_entries CSV
 * export) into Customer_Fonix_EntryData. Upload → summary → confirm →
 * delete + insert in one transaction → result. The file is sent twice
 * (analyze and execute); nothing is kept on the server in between.
 */

interface Summary {
  csvRows: number;
  callRows: number;
  totalEntries: number;
  limitReachedRows: number;
  skippedRows: number;
  skippedByStatus: Record<string, number>;
  freeEntries: number;
  orphanFreeEntries: number;
  duplicateCallIds: number;
  combinations: string[];
  shortcode: string;
  keyword: string;
  calledNumbers: string[];
  minStartDate: string | null;
  maxStartDate: string | null;
  existingRows: number;
  existingPostdataRows: number;
  postdataRowsToInsert: number;
  postdataByOutcome: Record<string, number>;
}

interface Result {
  deleted: number;
  inserted: number;
  postdataDeleted: number;
  postdataInserted: number;
  totalEntries: number;
  shortcode: string;
  keyword: string;
  topCallers: { callingNumber: string; calls: number; entries: number }[];
}

async function post<T>(path: string, file: File): Promise<T> {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`/api/admin/fonix-entry-import/${path}`, { method: "POST", credentials: "include", body: fd });
  const text = await res.text();
  let data: unknown = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = null; }
  if (!res.ok) {
    const d = data as { error?: string; message?: string } | null;
    throw new Error(d?.message ? `${d.error ?? "error"}: ${d.message}` : d?.error ?? text ?? `Request failed (${res.status})`);
  }
  return data as T;
}

export function FonixEntryImportPage() {
  const [file, setFile] = useState<File | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const analyze = async (f: File) => {
    setFile(f);
    setSummary(null);
    setResult(null);
    setError(null);
    setBusy(true);
    try {
      setSummary(await post<Summary>("analyze", f));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Analysis failed");
    } finally {
      setBusy(false);
    }
  };

  const execute = async () => {
    if (!file) return;
    setConfirmOpen(false);
    setBusy(true);
    setError(null);
    try {
      setResult(await post<Result>("execute", file));
      setSummary(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader title="Fonix Entry Import" />
      <div className="dashboard" style={{ display: "grid", gap: 16 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", color: "#8a5a00", background: "#fff4e0", border: "1px solid #f0c36d", borderRadius: 8, padding: 10, fontSize: "0.9rem" }}>
          <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <strong>Temporary tool.</strong> Imports a Fonix <code>web_entries</code> CSV export into <code>Customer_Fonix_EntryData</code>, plus one
<code>Customer_Fonix_Postdata</code> row per post in the file (entries, bonus entries and rejected posts, with the response
            from the export; customer 670) so the stats add up. Existing entries of the campaign
            (shortcode + keyword) and their Postdata are <strong>replaced</strong>. One campaign per file.
          </div>
        </div>

        <section className="tile" style={{ display: "grid", gap: 10 }}>
          <header className="tile-head"><h3>1. Upload</h3></header>
          <input type="file" accept=".csv,text/csv" disabled={busy} onChange={(e) => { const f = e.target.files?.[0]; if (f) void analyze(f); }} />
          {busy && !summary && !result && <div style={{ color: "var(--portal-muted)" }}>Working…</div>}
          {error && <div className="alert alert-error" style={{ whiteSpace: "pre-wrap" }}>{error}</div>}
        </section>

        {summary && (
          <section className="tile" style={{ display: "grid", gap: 12 }}>
            <header className="tile-head"><h3>2. Summary (nothing written yet)</h3></header>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10, fontSize: "0.9rem" }}>
              <Stat label="CSV rows" value={summary.csvRows} />
              <Stat label="Call rows to insert" value={summary.callRows} />
              <Stat label="Total entries" value={summary.totalEntries} />
              <Stat label="Limit reached (entryCount 0)" value={summary.limitReachedRows} />
              <Stat label="Bonus entries folded" value={summary.freeEntries} />
              <Stat label="Skipped rows" value={summary.skippedRows} />
              <Stat label="Duplicate call_id (kept first)" value={summary.duplicateCallIds} />
              <Stat label="Postdata rows to insert" value={summary.postdataRowsToInsert} />
              <Stat label="Existing entries (will be deleted)" value={summary.existingRows} tone={summary.existingRows > 0 ? "warn" : undefined} />
              <Stat label="Existing Postdata (will be deleted)" value={summary.existingPostdataRows} tone={summary.existingPostdataRows > 0 ? "warn" : undefined} />
            </div>
            <dl style={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: "6px 16px", margin: 0, fontSize: "0.9rem" }}>
              <dt style={{ color: "var(--portal-muted)" }}>Campaign</dt><dd style={{ margin: 0 }}>{summary.shortcode} / {summary.keyword}</dd>
              <dt style={{ color: "var(--portal-muted)" }}>Called numbers</dt><dd style={{ margin: 0 }}>{summary.calledNumbers.join(", ") || "—"}</dd>
              <dt style={{ color: "var(--portal-muted)" }}>Date range (UTC)</dt>
              <dd style={{ margin: 0 }}>{summary.minStartDate ? `${fmtUtc(summary.minStartDate)} → ${fmtUtc(summary.maxStartDate)}` : "—"}</dd>
              {Object.keys(summary.skippedByStatus).length > 0 && (
                <><dt style={{ color: "var(--portal-muted)" }}>Skipped by status</dt><dd style={{ margin: 0 }}>{Object.entries(summary.skippedByStatus).map(([k, v]) => `${k}: ${v}`).join(", ")}</dd></>
              )}
              <dt style={{ color: "var(--portal-muted)" }}>Postdata by outcome</dt>
              <dd style={{ margin: 0 }}>{Object.entries(summary.postdataByOutcome).map(([k, v]) => `${k}: ${v}`).join(", ") || "—"}</dd>
              {summary.orphanFreeEntries > 0 && (
                <><dt style={{ color: "#8a5a00" }}>Bonus entries without parent</dt><dd style={{ margin: 0, color: "#8a5a00" }}>{summary.orphanFreeEntries} (ignored)</dd></>
              )}
            </dl>
            <div>
              <button type="button" className="btn btn-danger" onClick={() => setConfirmOpen(true)} disabled={busy} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Upload size={14} /> Replace existing entries
              </button>
            </div>
          </section>
        )}

        {result && (
          <section className="tile" style={{ display: "grid", gap: 12 }}>
            <header className="tile-head"><h3>3. Result</h3></header>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10, fontSize: "0.9rem" }}>
              <Stat label="Entries deleted" value={result.deleted} />
              <Stat label="Entries inserted" value={result.inserted} />
              <Stat label="Total entries" value={result.totalEntries} />
              <Stat label="Postdata deleted" value={result.postdataDeleted} />
              <Stat label="Postdata inserted" value={result.postdataInserted} />
            </div>
            <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>Top 10 callers ({result.shortcode} / {result.keyword})</div>
            <table style={{ borderCollapse: "collapse", fontSize: "0.85rem", maxWidth: 520 }}>
              <thead>
                <tr>
                  <th style={th}>Calling number</th>
                  <th style={{ ...th, textAlign: "right" }}>Calls</th>
                  <th style={{ ...th, textAlign: "right" }}>Entries</th>
                </tr>
              </thead>
              <tbody>
                {result.topCallers.map((c) => (
                  <tr key={c.callingNumber}>
                    <td style={td}>{c.callingNumber}</td>
                    <td style={{ ...td, textAlign: "right" }}>{c.calls}</td>
                    <td style={{ ...td, textAlign: "right" }}>{c.entries}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>

      <ConfirmModal open={confirmOpen} onClose={() => setConfirmOpen(false)} title="Replace existing entries" confirmLabel="Replace" onConfirm={() => void execute()} busy={busy}>
        {summary && (
          <>
            This deletes <strong>{summary.existingRows}</strong> existing entries and <strong>{summary.existingPostdataRows}</strong> Postdata rows for{" "}
            <strong>{summary.shortcode} / {summary.keyword}</strong>, then inserts <strong>{summary.callRows}</strong> call rows ({summary.totalEntries} entries)
            and <strong>{summary.postdataRowsToInsert}</strong> Postdata rows, all in one transaction. On any error nothing is changed.
          </>
        )}
      </ConfirmModal>
    </>
  );
}

const th: React.CSSProperties = { textAlign: "left", padding: "6px 10px", borderBottom: "1px solid var(--portal-border)", color: "var(--portal-muted)", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em" };
const td: React.CSSProperties = { padding: "5px 10px", borderBottom: "1px solid var(--portal-border)" };

function fmtUtc(iso: string | null): string {
  // The values are UTC and must stay UTC on screen (fmtDateTime shifts to the session zone).
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/.exec(iso);
  return m ? `${m[3]}-${m[2]}-${m[1]} ${m[4]}:${m[5]}:${m[6]}` : fmtDateTime(iso);
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: "warn" }) {
  return (
    <article style={{ margin: 0, padding: "8px 12px", borderRadius: 10, border: `1px solid ${tone === "warn" ? "#f0c36d" : "var(--portal-primary-faint)"}`, background: tone === "warn" ? "#fff4e0" : "transparent" }}>
      <div style={{ color: "var(--portal-muted)", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: "1.3rem", fontWeight: 700 }}>{value.toLocaleString("en-GB")}</div>
    </article>
  );
}
